import React, { useEffect, useState, useRef } from "react";
import {
	Typography,
	Grid,
	Checkbox,
	Stack,
	CircularProgress,
	Snackbar,
	Alert,
	Divider,
} from "@mui/material";
import { StyledTextBox } from "../../../components/TextBox";
import { ContainedSearchButton } from "../../../components/Buttons";
import {
	GetBusinessInformation,
	UpdateBusinessInformation,
} from "../../../services/client.services";
import { LoadingBox } from "../../../components/Box";
import { useParams } from "react-router-dom";

export default function BusinessInformation() {
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [fetchError, setFetchError] = useState(false);
	const [formFields, setFormFields] = useState(null);
	const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const fetchData = async () => {
			setIsLoading(true);
			try {
				const data = await GetBusinessInformation(id);
				if (data) {
					setFormFields({
						name: { value: data.name || "", error: false, message: "" },
						email: { value: data.email || "", error: false, message: "" },
						businessType: { value: data.businessType || "", error: false, message: "" },
						address: { value: data.address || "", error: false, message: "" },
						country: { value: data.country || "", error: false, message: "" },
						city: { value: data.city || "", error: false, message: "" },
						state: { value: data.state || "", error: false, message: "" },
						contactNo: { value: data.contactNo || "", error: false, message: "" },
						connectedMarketPlaces: { value: data.connectedMarketPlaces || "", error: false, message: "" },
						idPassportDl: { value: data.owner.idPassportDl || "", error: false, message: "" },
						nationality: { value: data.owner.nationality || "", error: false, message: "" },
						dateOfBirth: { value: data.owner.dateOfBirth || "", error: false, message: "" },
						contactNumber: { value: data.owner.contactNumber || "", error: false, message: "" },
						ssn: { value: data.owner.ssn || "", error: false, message: "" },
						ownerEmail: { value: data.owner.email || "", error: false, message: "" },
						ownerAddress: { value: data.owner.address || "", error: false, message: "" },
						ownerCity: { value: data.owner.city || "", error: false, message: "" },
						ownerCountry: { value: data.owner.country || "", error: false, message: "" },
						ownerState: { value: data.owner.state || "", error: false, message: "" },
						ownerZipCode: { value: data.owner.zipCode || "", error: false, message: "" },
						fname: { value: data.owner.fname || "", error: false, message: "" },
						lname: { value: data.owner.lname || "", error: false, message: "" },
						idType: { value: data.owner.idType || "", error: false, message: "" },
						id: { value: data.id || "", error: false, message: "" },
						ownerId: { value: data.owner.id || "", error: false, message: "" },
						registrationNo: { value: data.registrationNo || "", error: false, message: "" },
						zipCode: { value: data.zipCode || "", error: false, message: "" },
						industry: { value: data.industry || "", error: false, message: "" },
						storeLink: { value: data.storeLink || "", error: false, message: "" },
						logisticAndFulfilment: { value: data.logisticAndFulfilment.join(', ') || "", error: false, message: "" },
					});
				} else {
					setFetchError(true)
				}
			} catch (error) {
				console.error("Error fetching client data", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [id]);

	const updateField = (field, value) => {
		setFormFields((prev) => ({
			...prev,
			[field]: {
				...prev[field],
				value,
				error: false,
				message: "",
			},
		}));
	};

	const validate = () => {
		let valid = true;
		const updatedFields = { ...formFields };

		const requiredFields = ["name", "email", "address", "country", "contactNo"];
		requiredFields.forEach((field) => {
			if (!formFields[field].value.trim()) {
				updatedFields[field].error = true;
				updatedFields[field].message = "This field is required.";
				valid = false;
			}
		});

		setFormFields(updatedFields);
		return valid;
	};

	const handleSave = async () => {
		if (!validate()) {
			setSnackbar({ open: true, message: "Please fix validation errors.", severity: "error" });
			return;
		}

		const payload = {
			id: formFields.id.value,
			name: formFields.name.value,
			businessType: formFields.businessType.value,
			email: formFields.email.value,
			address: formFields.address.value,
			country: formFields.country.value,
			contactNo: formFields.contactNo.value,
			connectedMarketPlaces: formFields.connectedMarketPlaces.value,
			industry: formFields.industry.value,
			city: formFields.city.value,
			state: formFields.state.value,
			owner: {
				id: formFields.ownerId.value,
				idPassportDl: formFields.state.value,
				nationality: formFields.nationality.value,
				dateOfBirth: formFields.dateOfBirth.value,
				contactNumber: formFields.contactNumber.value,
				ssn: formFields.ssn.value,
				email: formFields.ownerEmail.value,
				address: formFields.ownerAddress.value,
				city: formFields.ownerCity.value,
				country: formFields.ownerCountry.value,
				state: formFields.ownerState.value,
				zipCode: formFields.ownerZipCode.value,
				fname: formFields.fname.value,
				lname: formFields.lname.value,
				idType: formFields.idType.value,
			}
		};

		try {
			setIsLoading(true);
			await UpdateBusinessInformation(payload).then((data) => {
				if (data.response.status === 200) {
					setSnackbar({ open: true, message: "Business information updated successfully!", severity: "success" });
				} else {
					setSnackbar({ open: true, message: "Something went wrong while saving.", severity: "error" });
				}

			});
		} catch (error) {
			console.error("Failed to update business info", error);
			setSnackbar({ open: true, message: "Something went wrong while saving.", severity: "error" });
		} finally {
			setIsLoading(false);
		}
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	if (!formFields || isLoading) {
		return (
			<LoadingBox>
				{fetchError ?
					(
						<Typography variant="body2" color="neutral.letter_Black">
							Seller Business details Not Found
						</Typography>
					) :
					(
						<CircularProgress />
					)
				}
			</LoadingBox>
		);
	}

	return (
		<div>
			<Typography variant="body1">
				Basic Information for Business Profile.
			</Typography>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Business Name</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.name.value}
							error={formFields.name.error}
							helperText={formFields.name.message}
							onChange={(e) => updateField("name", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Segment</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.industry.value}
							error={formFields.industry.error}
							helperText={formFields.industry.message}
							onChange={(e) => updateField("industry", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Address + Country */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Registration Number</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.registrationNo.value}
							error={formFields.registrationNo.error}
							helperText={formFields.registrationNo.message}
							onChange={(e) => updateField("registrationNo", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Business Type</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.businessType.value}
							error={formFields.businessType.error}
							helperText={formFields.businessType.message}
							onChange={(e) => updateField("businessType", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Contact + Marketplaces */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Connected Market Places</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.connectedMarketPlaces.value}
							error={formFields.connectedMarketPlaces.error}
							helperText={formFields.connectedMarketPlaces.message}
							onChange={(e) => updateField("connectedMarketPlaces", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Store Front Link</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.storeLink.value}
							error={formFields.storeLink.error}
							helperText={formFields.storeLink.message}
							onChange={(e) => updateField("storeLink", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br />
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">logistic And Fulfilment</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.logisticAndFulfilment.value}
							error={formFields.logisticAndFulfilment.error}
							helperText={formFields.logisticAndFulfilment.message}
							onChange={(e) => updateField("logisticAndFulfilment", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
			</Grid>
			
			<br></br>
			<Divider></Divider>
			<br></br>
			<Typography variant="body1">
				Business Contact Information
			</Typography>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Contact Number</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.contactNo.value}
							error={formFields.contactNo.error}
							helperText={formFields.contactNo.message}
							onChange={(e) => updateField("contactNo", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Email</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.email.value}
							error={formFields.email.error}
							helperText={formFields.email.message}
							onChange={(e) => updateField("email", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Address + Country */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Business Address</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.address.value}
							error={formFields.address.error}
							helperText={formFields.address.message}
							onChange={(e) => updateField("address", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">City</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.city.value}
							error={formFields.city.error}
							helperText={formFields.city.message}
							onChange={(e) => updateField("city", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Contact + Marketplaces */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">State</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.state.value}
							error={formFields.state.error}
							helperText={formFields.state.message}
							onChange={(e) => updateField("state", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Zip code</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.zipCode.value}
							error={formFields.zipCode.error}
							helperText={formFields.zipCode.message}
							onChange={(e) => updateField("zipCode", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Industry */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Country</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.country.value}
							onChange={(e) => updateField("country", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Divider></Divider>
			<br></br>
			<Typography variant="body1">
				Business Owner Contact Information
			</Typography>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">First Name</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.fname.value}
							error={formFields.fname.error}
							helperText={formFields.fname.message}
							onChange={(e) => updateField("fname", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Last Name</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.lname.value}
							error={formFields.lname.error}
							helperText={formFields.lname.message}
							onChange={(e) => updateField("lname", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Address + Country */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Identification </Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.idPassportDl.value}
							error={formFields.idPassportDl.error}
							helperText={formFields.idPassportDl.message}
							onChange={(e) => updateField("idPassportDl", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Nationality</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.nationality.value}
							error={formFields.nationality.error}
							helperText={formFields.nationality.message}
							onChange={(e) => updateField("nationality", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />

			{/** Contact + Marketplaces */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Date Of Birth</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.dateOfBirth.value}
							error={formFields.dateOfBirth.error}
							helperText={formFields.dateOfBirth.message}
							onChange={(e) => updateField("dateOfBirth", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Social Security Number</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ssn.value}
							error={formFields.ssn.error}
							helperText={formFields.ssn.message}
							onChange={(e) => updateField("ssn", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Contact Number</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.contactNumber.value}
							error={formFields.contactNumber.error}
							helperText={formFields.contactNumber.message}
							onChange={(e) => updateField("contactNumber", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Email</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerEmail.value}
							error={formFields.ownerEmail.error}
							helperText={formFields.ownerEmail.message}
							onChange={(e) => updateField("ownerEmail", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br />
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Address</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerAddress.value}
							error={formFields.ownerAddress.error}
							helperText={formFields.ownerAddress.message}
							onChange={(e) => updateField("ownerAddress", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">City</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerCity.value}
							error={formFields.ownerCity.error}
							helperText={formFields.ownerCity.message}
							onChange={(e) => updateField("ownerCity", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br />
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">State</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerState.value}
							error={formFields.ownerState.error}
							helperText={formFields.ownerState.message}
							onChange={(e) => updateField("ownerState", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Zip code</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerZipCode.value}
							error={formFields.ownerZipCode.error}
							helperText={formFields.ownerZipCode.message}
							onChange={(e) => updateField("ownerZipCode", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br />
			{/** Industry */}
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2">Country</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.ownerCountry.value}
							error={formFields.ownerCountry.error}
							helperText={formFields.ownerCountry.message}
							onChange={(e) => updateField("ownerCountry", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br />
			<Stack direction="row" spacing={1} alignItems="center">
				<Checkbox />
				<Typography variant="body2">
					Notify the changes for user.
				</Typography>
			</Stack>

			<br />
			<ContainedSearchButton variant="contained" onClick={handleSave}>
				Save changes
			</ContainedSearchButton>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					sx={{ width: "100%" }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
